/**
 * Row mapping — the one place that knows column names map to camelCase fields.
 * Shared by the repositories so no service ever sees a raw database row.
 */

import type { MediaItem, MediaStatus, MediaType, Project, Bucket, Source } from '../types';
import type { SqlRow } from '../adapters/sqlite-client';

const num = (v: unknown): number => Number(v ?? 0);
const numOrNull = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));
const str = (v: unknown): string => String(v ?? '');
const strOrNull = (v: unknown): string | null => (v === null || v === undefined ? null : String(v));

export function toProject(row: SqlRow): Project {
  return {
    id: num(row.id),
    name: str(row.name),
    autoAdvance: num(row.auto_advance) === 1,
    previewTargetPx: num(row.preview_target_px),
    createdAt: num(row.created_at),
    updatedAt: num(row.updated_at),
    lastOpenedAt: numOrNull(row.last_opened_at),
    lastViewedMediaId: numOrNull(row.last_viewed_media_id),
    itemCount: num(row.item_count),
    reviewedCount: num(row.reviewed_count),
    keptCount: num(row.kept_count),
  };
}

export function toMedia(row: SqlRow): MediaItem {
  return {
    id: num(row.id),
    projectId: num(row.project_id),
    sourceId: numOrNull(row.source_id),
    relPath: str(row.rel_path),
    filename: str(row.filename),
    type: str(row.type) as MediaType,
    ext: str(row.ext),
    sizeBytes: numOrNull(row.size_bytes),
    width: numOrNull(row.width),
    height: numOrNull(row.height),
    durationMs: numOrNull(row.duration_ms),
    thumbKey: strOrNull(row.thumb_key),
    previewKey: strOrNull(row.preview_key),
    status: str(row.status) as MediaStatus,
    statusCheckedAt: numOrNull(row.status_checked_at),
    bucketId: numOrNull(row.bucket_id),
    rotation: num(row.rotation),
    createdAt: num(row.created_at),
    modifiedAt: numOrNull(row.modified_at),
  };
}

export function toBucket(row: SqlRow): Bucket {
  return {
    id: num(row.id),
    projectId: num(row.project_id),
    name: str(row.name),
    kind: str(row.kind) as 'keep' | 'reject',
    hotkey: str(row.hotkey),
    displayOrder: num(row.display_order),
    count: num(row.count),
  };
}

export function toSource(row: SqlRow): Source {
  return {
    id: num(row.id),
    projectId: num(row.project_id),
    name: str(row.name),
    addedAt: num(row.added_at),
  };
}
